import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';

interface GalleryImage {
  id: number;
  url: string;
  caption: string;
  sort_order: number;
}

export default function ManageGallery() {
  const [token, setToken] = useState<string | null>(null);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newCaption, setNewCaption] = useState('');
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = localStorage.getItem('marinelink_owner_token');
    if (!t) { navigate('/signin'); return; }
    setToken(t);
  }, [navigate]);

  useEffect(() => {
    if (!token) return;
    fetch('/.netlify/functions/auth-gallery', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(d => { if (d.error) setError(d.error); else setImages(d); })
      .catch(() => setError('Failed to load gallery'))
      .finally(() => setLoading(false));
  }, [token]);

  const addImage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUrl.trim() || !token) return;
    setAdding(true);
    setError('');
    try {
      const res = await fetch('/.netlify/functions/auth-gallery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: newUrl.trim(), caption: newCaption.trim() || null, sort_order: images.length }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to add image'); return; }
      setImages([...images, data.image]);
      setNewUrl('');
      setNewCaption('');
    } catch {
      setError('Network error');
    } finally {
      setAdding(false);
    }
  };

  const deleteImage = async (id: number) => {
    if (!token) return;
    if (!confirm('Remove this image from your gallery?')) return;
    try {
      const res = await fetch(`/.netlify/functions/auth-gallery?id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); setError(d.error || 'Failed to delete'); return; }
      setImages(images.filter(img => img.id !== id));
    } catch {
      setError('Network error');
    }
  };

  const updateCaption = async (id: number, caption: string) => {
    if (!token) return;
    try {
      await fetch('/.netlify/functions/auth-gallery', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id, caption }),
      });
      setImages(images.map(img => img.id === id ? { ...img, caption } : img));
    } catch {
      setError('Failed to update caption');
    }
  };

  const moveImage = async (id: number, direction: 'up' | 'down') => {
    const idx = images.findIndex(img => img.id === id);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= images.length) return;
    const newImages = [...images];
    [newImages[idx], newImages[newIdx]] = [newImages[newIdx], newImages[idx]];
    // Reassign sort_order
    const reordered = newImages.map((img, i) => ({ ...img, sort_order: i }));
    setImages(reordered);
    // Save new sort orders
    if (!token) return;
    for (const img of reordered) {
      try {
        await fetch('/.netlify/functions/auth-gallery', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ id: img.id, sort_order: img.sort_order }),
        });
      } catch { /* best effort */ }
    }
  };

  if (loading) return <div className="max-w-[800px] mx-auto px-4 py-12 text-center text-text-muted">Loading gallery…</div>;

  return (
    <div className="max-w-[900px] mx-auto px-4 md:px-8 py-6 md:py-12 pb-24">
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/dashboard" className="text-text-secondary hover:text-accent transition-colors">Dashboard</Link>
        <span>/</span>
        <span>Manage Gallery</span>
      </div>
      <h1 className="font-display text-2xl md:text-[1.8rem] font-bold mb-6">Photo Gallery</h1>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Add new image */}
      <form onSubmit={addImage} className="bg-card-light dark:bg-card border border-[rgba(79,179,217,0.14)] rounded-lg p-4 mb-6">
        <h2 className="font-semibold text-sm mb-3">Add Image</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Image URL</label>
            <input
              className="form-input"
              type="url"
              required
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder="https://example.com/photo.jpg"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Caption (optional)</label>
            <input
              className="form-input"
              value={newCaption}
              onChange={e => setNewCaption(e.target.value)}
              placeholder="e.g. Our workshop floor"
              maxLength={200}
            />
          </div>
          <button type="submit" disabled={adding} className="btn btn-primary btn-sm">
            {adding ? 'Adding…' : '+ Add image'}
          </button>
        </div>
        <p className="text-xs text-text-muted mt-2">
          Tip: Upload your photos to an image host (Imgur, Cloudinary, your own website) and paste the direct URL here.
        </p>
      </form>

      {/* Gallery grid */}
      {images.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto mb-3 opacity-40">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <p>No gallery images yet. Add your first photo above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((img, idx) => (
            <div key={img.id} className="bg-card-light dark:bg-card border border-[rgba(79,179,217,0.14)] rounded-lg overflow-hidden">
              <div className="relative aspect-[4/3] bg-surface">
                <img src={img.url} alt={img.caption || 'Gallery image'} className="w-full h-full object-cover" />
                <div className="absolute top-2 right-2 flex gap-1">
                  <button
                    onClick={() => moveImage(img.id, 'up')}
                    disabled={idx === 0}
                    className="w-7 h-7 rounded-md bg-black/50 text-white flex items-center justify-center hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Move up"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="m18 15-6-6-6 6" /></svg>
                  </button>
                  <button
                    onClick={() => moveImage(img.id, 'down')}
                    disabled={idx === images.length - 1}
                    className="w-7 h-7 rounded-md bg-black/50 text-white flex items-center justify-center hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Move down"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="m6 9 6 6 6-6" /></svg>
                  </button>
                  <button
                    onClick={() => deleteImage(img.id)}
                    className="w-7 h-7 rounded-md bg-red-500/80 text-white flex items-center justify-center hover:bg-red-600"
                    aria-label="Delete image"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                  </button>
                </div>
                <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/50 text-white text-xs">#{idx + 1}</span>
              </div>
              <div className="p-3">
                <input
                  className="form-input text-sm"
                  value={img.caption || ''}
                  onChange={e => {
                    const val = e.target.value;
                    setImages(images.map(im => im.id === img.id ? { ...im, caption: val } : im));
                  }}
                  onBlur={e => updateCaption(img.id, e.target.value)}
                  placeholder="Caption…"
                  maxLength={200}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6">
        <Link to="/dashboard" className="btn btn-ghost btn-sm">← Back to dashboard</Link>
      </div>
    </div>
  );
}
